import styles from './Roster.module.css';

import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import CardMedia from '@mui/material/CardMedia';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Check from '@mui/icons-material/CheckCircle';
import Clock from '@mui/icons-material/AccessTime';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import TimerOffIcon from '@mui/icons-material/TimerOff';

import { red } from '@mui/material/colors';

import React from 'react';

import { DateTime } from 'luxon';

import { getPatroller, getRole, getShift } from "../../api.js";

import { useAuth } from "../../AuthContext";
import { useRealtimeTrigger } from '../../hooks/useRealtimeTrigger';
import { Timelog } from '../Timelog/Timelog';

// Module-level cache that persists across component mounts
const patrollerCache = {};

export const Roster = ({ shift }) => {
    const [ error, setError ] = React.useState(null);
    const [ isLoading, setIsLoading ] = React.useState(true);
    const [ roster, setRoster ] = React.useState([]);
    const [ timelog, setTimelog ] = React.useState(null);
    const [ currentShift, setCurrentShift ] = React.useState(shift);
    const [ patroller, setPatroller ] = React.useState(null);
    const [ imageLoading, setImageLoading ] = React.useState(true);

    const { token } = useAuth();

    const memoizedPatrollers = React.useMemo(() => [ ...currentShift.patrollers ], [ currentShift.patrollers ]);

    React.useEffect(() => {
        const controller = new AbortController();

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const rosterData = await Promise.all(
                    memoizedPatrollers.map(async (p) => {
                        const cacheKey = `${ p.id }_${ p.role }`;

                        // Check cache first
                        if (patrollerCache[ cacheKey ]) {
                            return patrollerCache[ cacheKey ];
                        }

                        const patroller = await getPatroller(p.id, token);
                        const role = p.role === 'unscheduled' ? { name: 'Unscheduled' } : await getRole(p.role, token);

                        const data = {
                            id: patroller.id,
                            role: role.name,
                            firstName: patroller.firstName,
                            lastName: patroller.lastName,
                            photoURL: patroller.photoURL,
                            city: patroller?.city ? patroller.privacy?.city ? patroller.city : "(shh... it's private)" : 'nowhere apparently...',
                            aboutMe: patroller.aboutMe,
                            startDate: DateTime.fromISO(patroller.startDate).isValid ? DateTime.fromISO(patroller?.startDate).toFormat('MMM yyyy') : 'the beginning of time',
                        };

                        // Store in cache
                        patrollerCache[ cacheKey ] = data;

                        return data;
                    })
                );

                setRoster(rosterData.sort((a, b) => {
                    if (a.role < b.role) return -1;
                    if (a.role > b.role) return 1;

                    // Then by lastName if roles are equal
                    if (a.lastName < b.lastName) return -1;
                    if (a.lastName > b.lastName) return 1;

                    return 0;
                }));
            } catch (error) {
                if (error.name !== 'AbortError') {
                    setError('Failed to fetch patrollers');
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();

        return () => {
            controller.abort();
        };
    }, [ memoizedPatrollers, token ]);
    
    const handleTimelogUpdate = React.useCallback(async (shiftId) => {
        if (shiftId !== currentShift.id) return; // skip if not for current shift

        try {
            const updated_shift = await getShift(shiftId, token);

            if (updated_shift && !updated_shift.error) {
                setCurrentShift(updated_shift);
            } else {
                console.error('Invalid shift data or error:', updated_shift);
            }
        } catch (error) {
            console.error('Failed to update shift:', error);
        }
    }, [ token, currentShift.id ]);

    useRealtimeTrigger('manager', 'timelogs', handleTimelogUpdate);

    if (isLoading) {
        return <span>Loading roster...</span>;
    }

    if (error) {
        return <span>{ error }</span>;
    }

    if (!roster || roster.length === 0) {
        return <span>No roster found for this shift</span>;
    }

    return (
        <>
            <div className={ styles.roster }>
                { roster?.map(patroller => {
                    const p_timelog = currentShift.timelogs?.find(t => t.patroller === patroller.id);
                    const clockedInTime = p_timelog ? DateTime.fromMillis(p_timelog.started).toLocaleString(DateTime.TIME_SIMPLE) : null;
                    const clockedOutTime = p_timelog && p_timelog.stopped ? DateTime.fromMillis(p_timelog.stopped).toLocaleString(DateTime.TIME_SIMPLE) : null;

                    if (p_timelog) {
                        p_timelog.patrollerName = `${ patroller.firstName } ${ patroller.lastName }`;
                    }

                    return (
                        <div key={ patroller.id } className={ styles.patrollers }>
                            <div onClick={ () => { setPatroller(patroller) } }>
                                <Avatar variant='rounded' src={ patroller.photoURL } />

                                <span>{ `${ patroller.firstName?.charAt(0) } ${ patroller.lastName }` }</span>
                            </div>

                            <Stack direction='row' spacing={ 1 } alignItems='center'>
                                <Typography alignContent='center' component='span' onClick={ () => { setPatroller(patroller) } } >
                                    { patroller.role }
                                </Typography>

                                { p_timelog ? (
                                    p_timelog.stopped ?
                                        <Check onClick={ () => setTimelog(p_timelog) } titleAccess={ `Clocked in at ${ clockedInTime }\nClocked out at ${ clockedOutTime }` } fontSize='small' color='success' /> :
                                        <Clock onClick={ () => setTimelog(p_timelog) } titleAccess={ `Clocked in at ${ clockedInTime }` } fontSize='small' color='info' />
                                ) : (
                                    DateTime.fromISO(currentShift.date) > DateTime.now() ?
                                        null :
                                        DateTime.fromMillis(currentShift.start) > DateTime.now() ?
                                            <HourglassBottomIcon onClick={ () => setTimelog(null) } titleAccess="Shift not started yet" fontSize='small' color='primary' /> :
                                            <TimerOffIcon onClick={ () => setTimelog(null) } titleAccess="No timelog recorded" fontSize='small' color='warning' />
                                ) }
                            </Stack>
                        </div>
                    );
                }) }
            </div>

            <Dialog
                open={ !!patroller }
                onClose={ () => setPatroller(null) }
            >
                <DialogContent>
                    <Card sx={ { minWidth: 200, maxWidth: 300 } }>
                        <CardActionArea onClick={ () => setPatroller(null) }>
                            <CardHeader
                                avatar={
                                    <Avatar sx={ { bgcolor: red[ 500 ], color: '#fff' } } aria-label="patroller">
                                        { patroller?.firstName?.charAt(0) }{ patroller?.lastName?.charAt(0) }
                                    </Avatar>
                                }
                                title={ `${ patroller?.firstName } ${ patroller?.lastName }` }
                                subheader={ `From ${ patroller?.city }\nPatrolling since ${ patroller?.startDate }` }
                                slotProps={ { subheader: { style: { whiteSpace: 'pre-line' } } } }
                            />

                            <CardMedia
                                component="img"
                                image={ patroller?.photoURL }
                                alt={ `${ patroller?.firstName } ${ patroller?.lastName }` }
                                onLoad={ () => setImageLoading(false) }
                                onError={ () => setImageLoading(false) }
                                sx={ { width: '100%', aspectRatio: 1, objectFit: 'cover', borderTop: '1px solid #333', borderBottom: '1px solid #333', boxSizing: 'border-box' } }
                            />

                            { imageLoading && (
                                <Skeleton
                                    variant="rectangular"
                                    height={ 140 }
                                    sx={ { width: '100%', aspectRatio: 1, position: 'absolute', top: 0, left: 0, zIndex: 1 } }
                                />
                            ) }

                            <CardContent>
                                <Typography variant="body2" sx={ { textAlign: 'justify', color: 'text.secondary' } }>
                                    { patroller?.aboutMe }
                                </Typography>
                            </CardContent>
                        </CardActionArea>

                        <CardActions>
                            <Button size="small" variant='outlined' onClick={ () => setPatroller(null) }>Close</Button>
                        </CardActions>
                    </Card>
                </DialogContent>
            </Dialog>

            <Dialog
                open={ !!timelog }
                onClose={ () => setTimelog(null) }
                maxWidth="sm"
                fullWidth
            >
                <DialogContent>
                    <Timelog shift={ currentShift } timelog={ timelog } onClose={ () => setTimelog(null) } />
                </DialogContent>

                <DialogActions>
                    <Button variant='outlined' onClick={ () => setTimelog(null) }>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};